import React, { useEffect, useState } from "react";
import axios from "axios";
import UpdateMembership from "./UpdateMembership";
import { config } from "../../../config";

const apiUrl = config.apiUrl;
const imageUrl = config.imageUrl;

const ViewMembership = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingMembership, setEditingMembership] = useState(null);
  const [photo, setPhoto] = useState(null);

  const [formData, setFormData] = useState({
    membership_type: "",
    image: null,
    membership_description: "",
    membership_duration: "",
    selling_price: "",
    discount_price: "",
    benefits: []
  });

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await axios.get(`${apiUrl}/memberships`);
        setMemberships(response.data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching memberships:", err);
        setError("Failed to load memberships. Please try again later.");
        setLoading(false);
      }
    };

    fetchMemberships();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this membership?")) {
      try {
        await axios.delete(`${apiUrl}/membershipdelete/${id}`);
        setMemberships(prev => prev.filter(m => m.id !== id));
      } catch (err) {
        console.error("Error deleting membership:", err);
        setError("Failed to delete membership. Please try again.");
      }
    }
  };

  const handleEditClick = (membership) => {
    if (!membership) return;
    
    if (editingMembership === membership.id) {
      setEditingMembership(null);
    } else {
      setEditingMembership(membership.id);
      setFormData({
        membership_type: membership.membership_type || "",
        image: membership.membership_image || null,
        membership_description: membership.membership_description || "",
        membership_duration: membership.membership_duration || "",
        selling_price: membership.selling_price || "",
        discount_price: membership.discount_price || "",
        benefits: membership.benefits?.map(b => ({
          id: b.id,
          benefit_name: b.benefit_name || "",
          description: b.description || "",
          icon: b.icon || "",
          is_active: b.is_active ?? true,
          sort_order: b.sort_order || 0
        })) || []
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleQuillChange = (value) => {
    setFormData(prev => ({
      ...prev,
      membership_description: value || ""
    }));
  };

  const handleBenefitChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedBenefits = [...formData.benefits];
    
    updatedBenefits[index] = {
      ...updatedBenefits[index],
      [name]: type === 'checkbox' ? checked : value
    };
    
    setFormData(prev => ({
      ...prev,
      benefits: updatedBenefits
    }));
  };

  const addBenefit = () => {
    setFormData(prev => ({
      ...prev,
      benefits: [
        ...prev.benefits,
        {
          benefit_name: "",
          description: "",
          icon: "",
          is_active: true,
          sort_order: prev.benefits.length
        }
      ]
    }));
  };

  const removeBenefit = (index) => {
    const updatedBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      benefits: updatedBenefits
    }));
  };


  console.log("Form Data:", formData);
  console.log("Photo:", photo);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const data = new FormData();
    
    data.append('_method', 'PUT');
    if (photo) data.append('image', photo);

    data.append("membership_type", formData.membership_type);
    data.append("membership_description", formData.membership_description);
    data.append("membership_duration", formData.membership_duration);
    data.append("selling_price", formData.selling_price);
    data.append("discount_price", formData.discount_price);
    
    formData.benefits.forEach((benefit, index) => {
      if (benefit.id) data.append(`benefits[${index}][id]`, benefit.id);
      data.append(`benefits[${index}][benefit_name]`, benefit.benefit_name);
      data.append(`benefits[${index}][description]`, benefit.description);
      data.append(`benefits[${index}][icon]`, benefit.icon || '');
      data.append(`benefits[${index}][is_active]`, benefit.is_active ? '1' : '0');
      data.append(`benefits[${index}][sort_order]`, benefit.sort_order);
    });

    try {
      const response = await axios.post(
        `${apiUrl}/membershipsupdate/${editingMembership}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMemberships(prev => 
        prev.map(m => 
          m.id === editingMembership ? response.data.data : m
        )
      );
      
      setEditingMembership(null);
      setPhoto(null);
    } catch (err) {
      console.error("Error updating membership:", err);
      setError(err.response?.data?.message || "Failed to update membership. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !editingMembership) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !editingMembership) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="float-right font-bold"
          >
            &times;
          </button>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">Membership List</h2>

      <div className="overflow-x-auto shadow-md rounded-lg">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 border-b text-left">Image</th>
              <th className="py-3 px-4 border-b text-left">Type</th>
              <th className="py-3 px-4 border-b text-left">Duration</th>
              <th className="py-3 px-4 border-b text-left">Price</th>
              <th className="py-3 px-4 border-b text-left">Benefits</th>
              <th className="py-3 px-4 border-b text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {memberships.length > 0 ? (
              memberships.map((membership) => (
                <tr key={membership.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 border-b">
                    {membership.membership_image && (
                      <img
                        src={`${imageUrl}/${membership.membership_image}`}
                        alt={membership.membership_type || "Membership"}
                        className="h-12 w-12 object-cover rounded"
                        onError={(e) => {
                          e.target.src = '/default-membership.png';
                          e.target.onerror = null;
                        }}
                      />
                    )}
                  </td>
                  <td className="py-3 px-4 border-b">
                    {membership.membership_type || "N/A"}
                  </td>
                  <td className="py-3 px-4 border-b">
                    {membership.membership_duration || "N/A"}
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex flex-col">
                      <span className="line-through text-gray-500">
                        {membership.selling_price ? `${membership.selling_price}৳` : "N/A"}
                      </span>
                      <span className="font-semibold">
                        {membership.discount_price ? `${membership.discount_price}৳` : "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b">
                    <ul className="list-disc list-inside text-sm">
                      {membership.benefits?.slice(0, 3).map((b, i) => (
                        <li key={i}>{b.benefit_name || "Unnamed Benefit"}</li>
                      ))}
                      {membership.benefits?.length > 3 && (
                        <li>+{membership.benefits.length - 3} more</li>
                      )}
                      {(!membership.benefits || membership.benefits.length === 0) && (
                        <li>No benefits</li>
                      )}
                    </ul>
                  </td>
                  <td className="py-3 px-4 border-b text-center">
                    <button
                      onClick={() => handleEditClick(membership)}
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(membership.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-4 text-center text-gray-500">
                  No memberships found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingMembership && (
        <div className="mt-8">
          <UpdateMembership
            formData={formData}
            handleChange={handleChange}
            handleQuillChange={handleQuillChange}
            handleBenefitChange={handleBenefitChange}
            handleSubmit={handleSubmit}
            addBenefit={addBenefit}
            removeBenefit={removeBenefit}
            setPhoto={setPhoto}
            loading={loading}
            error={error}
            onCancel={() => setEditingMembership(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ViewMembership;